import { Brandmark } from "#src/components/Presentational/Logos/Brandmark";
import {
  useAnnotationStore,
  useAnnotationToolProps,
} from "#src/stores/annotationStore";
import {
  Button,
  Card,
  DropdownMenu,
  Icon,
  LoadingState,
} from "@validereinc/common-components";
import {
  Canvas,
  FabricImage,
  FabricObject,
  FabricText,
  Group,
  Line,
  ModifiedEvent,
  Path,
  PencilBrush,
  Point,
  TPointerEvent,
  TPointerEventInfo,
  XY,
} from "fabric";
import React, { useEffect, useRef, useState } from "react";
import {
  addHalfImageBlockerRectangle,
  addUniquePixelPoint,
  aggregateFunctions,
  annotationToolConfig,
  annotationToolObjectIDs,
  findObjectById,
  findObjectsByIds,
  previewOnlyAttributes,
  resetView,
  sampleQuadraticCurve,
  updateDynamicColors,
} from "./FabricCTR.helpers";

// Config:
const {
  highDetailedBezierCurveSampling,
  tickLineThickness,
  aggregateLineThickness,
  dynamicColoursEnabled,
  defaultTickMarkerColour,
  maxCanvasHeight,
} = annotationToolConfig;

const NewAnnotationTool = () => {
  // Zustand store
  const {
    isDrawingEnabled,
    setIsDrawingEnabled,
    drawnPath,
    setDrawnPath,
    clearDrawnPath,
    tickVisibility,
    setTickVisibility,
    updateTickVisibility,
    currentBackgroundImage,
    toggleBackgroundImage,
    setResizeRatio,
    updateActiveMeasurement,
    imageURLs,
    isLoading,
    fabricCanvas,
    setFabricCanvas,
  } = useAnnotationStore();

  // Computed properties
  const { canDraw, canAddTicks, focusedHalf, enabled, aggregateFunction } =
    useAnnotationToolProps();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pathCreatedHandlerRef = useRef<
    ((event: { path: FabricObject }) => void) | null
  >(null);
  const aggregateLineY = useRef<number | undefined>();
  const [isCanvasReady, setIsCanvasReady] = useState(false);

  // Initialize the canvas and set the background image
  useEffect(() => {
    const canvas = new Canvas(canvasRef.current ?? undefined, {
      width: 500, // Initial size, will be updated after image loads
      height: 500,
    });

    setFabricCanvas(canvas);

    // Cleanup function
    return () => {
      canvas.dispose();
      setFabricCanvas(null);
    };
  }, []);

  useEffect(() => {
    if (!fabricCanvas || !imageURLs || !(imageURLs.regular && imageURLs.uv))
      return;

    FabricImage.fromURL(imageURLs[currentBackgroundImage], {
      crossOrigin: "anonymous",
    }).then((img) => {
      /** Fabric 7 defaults origin to center; use left/top so background aligns to canvas (0,0). */
      img.set({ originX: "left", originY: "top" });
      const imageAspectRatio = img.width / img.height;
      const originalWidth = img.width;

      let canvasWidth = img.width;
      let canvasHeight = img.height;

      // If image is taller than max height, scale it down
      if (canvasHeight > maxCanvasHeight) {
        canvasHeight = maxCanvasHeight;
        canvasWidth = maxCanvasHeight * imageAspectRatio;
      }

      // Calculate resize ratio
      const ratio = canvasWidth / originalWidth;

      setResizeRatio(ratio);

      // Set canvas dimensions
      fabricCanvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight,
      });

      // Scale the image to fit the fabricCanvas
      img.scaleToWidth(canvasWidth);
      img.scaleToHeight(canvasHeight);

      // Set as background image
      fabricCanvas.backgroundImage = img;

      fabricCanvas.renderAll();
      setIsCanvasReady(true);
    });
  }, [imageURLs, fabricCanvas, currentBackgroundImage]);

  const isDragging = useRef<boolean>(false);
  const lastPosX = useRef<number>(0);
  const lastPosY = useRef<number>(0);

  // Add tick groups to the canvas
  useEffect(() => {
    if (!fabricCanvas) return;
    const topTickLine = new Line([0, 0, 50, 0], {
      stroke: defaultTickMarkerColour,
      strokeWidth: tickLineThickness,
      strokeUniform: true,
      hasBorders: false,
      hasControls: false,
    });

    const topTickLineLabel = new FabricText("Top Tick", {
      left: 25, // Center it on the line (relative to group)
      top: 15, // Position above the line (relative to group)
      fontSize: 12,
      fontFamily: "IBM Plex Sans",
      fill: defaultTickMarkerColour,
      textAlign: "center",
      originX: "center",
      originY: "center",
    });

    // Group line 1 with its label
    const topTickGroup = new Group([topTickLine, topTickLineLabel], {
      left: -1000,
      top: -1000,
      /** Fabric 7 defaults origin to center; use left/top so .left/.top are top-left for measurements. */
      originX: "left",
      originY: "top",
      hasBorders: false,
      hasControls: false,
      selectable: true,
    });

    // Create line 2
    const bottomTickLine = new Line([0, 0, 50, 0], {
      stroke: defaultTickMarkerColour,
      strokeWidth: tickLineThickness,
      strokeUniform: true,
      hasBorders: false,
      hasControls: false,
    });

    const bottomTickLabel = new FabricText("Bottom Tick", {
      left: 25, // Center it on the line (relative to group)
      top: 15, // Position below the line (relative to group)
      fontSize: 12,
      fontFamily: "IBM Plex Sans",
      fill: defaultTickMarkerColour,
      textAlign: "center",
      originX: "center",
      originY: "center",
    });

    // Group line 2 with its label
    const bottomTickGroup = new Group([bottomTickLine, bottomTickLabel], {
      left: -10000,
      top: -10000,
      /** Fabric 7 defaults origin to center; use left/top so .left/.top are top-left for measurements. */
      originX: "left",
      originY: "top",
      hasBorders: false,
      hasControls: false,
      selectable: true,
    });

    // Add IDs for identification
    topTickGroup.set("id", annotationToolObjectIDs.TOP_TICK_GROUP);
    bottomTickGroup.set("id", annotationToolObjectIDs.BOTTOM_TICK_GROUP);

    const onTopTickMoveHandler = () => {
      setTimeout(
        () =>
          updateDynamicColors({
            groupId: annotationToolObjectIDs.TOP_TICK_GROUP,
            canvas: fabricCanvas,
          }),
        50
      );
    };
    const onBottomTickMoveHandler = () => {
      setTimeout(
        () =>
          updateDynamicColors({
            groupId: annotationToolObjectIDs.BOTTOM_TICK_GROUP,
            canvas: fabricCanvas,
          }),
        50
      );
    };

    const onTopTickModifiedHandler = (e: ModifiedEvent<TPointerEvent>) => {
      if (e.action === "drag") {
        const topTickY = topTickGroup.top;
        const topTickX = topTickGroup.left;
        // Gets triggered when tick is dropped, not on every drag event:
        updateActiveMeasurement({ topTickY, topTickX });
      }
    };

    const onBottomTickModifiedHandler = (e: ModifiedEvent<TPointerEvent>) => {
      if (e.action === "drag") {
        const bottomTickY = bottomTickGroup.top;
        const bottomTickX = bottomTickGroup.left;
        // Gets triggered when tick is dropped, not on every drag event:
        updateActiveMeasurement({ bottomTickY, bottomTickX });
      }
    };

    if (dynamicColoursEnabled) {
      topTickGroup.on("moving", onTopTickMoveHandler);
      bottomTickGroup.on("moving", onBottomTickMoveHandler);
    }

    // Add event listeners for when tick groups are dragged
    topTickGroup.on("modified", onTopTickModifiedHandler);
    bottomTickGroup.on("modified", onBottomTickModifiedHandler);

    fabricCanvas.add(topTickGroup);
    fabricCanvas.add(bottomTickGroup);

    return () => {
      if (dynamicColoursEnabled) {
        topTickGroup.off("moving", onTopTickMoveHandler);
        bottomTickGroup.off("moving", onBottomTickMoveHandler);
      }
      topTickGroup.off("modified", onTopTickModifiedHandler);
      bottomTickGroup.off("modified", onBottomTickModifiedHandler);
    };
  }, [fabricCanvas]);

  // Add event listeners to the canvas for moving around and zooming
  useEffect(() => {
    if (!fabricCanvas) return;
    const handleMouseWheel = (opt: TPointerEventInfo<WheelEvent>) => {
      const delta = opt.e.deltaY;
      let zoom = fabricCanvas.getZoom();
      zoom *= 0.999 ** delta;
      if (zoom > 20) zoom = 20;
      if (zoom < 1) zoom = 1;
      fabricCanvas.zoomToPoint(new Point(opt.e.offsetX, opt.e.offsetY), zoom);

      // Apply inverse scaling to tick groups to maintain visual size
      const topTickGroup = findObjectById(
        fabricCanvas,
        annotationToolObjectIDs.TOP_TICK_GROUP
      ) as Group;

      const bottomTickGroup = findObjectById(
        fabricCanvas,
        annotationToolObjectIDs.BOTTOM_TICK_GROUP
      ) as Group;

      if (topTickGroup && bottomTickGroup) {
        const inverseScale = 1 / zoom;

        // Apply inverse scaling to entire groups to maintain line length and text positioning
        topTickGroup.set({
          scaleX: inverseScale,
          scaleY: inverseScale,
        });

        bottomTickGroup.set({
          scaleX: inverseScale,
          scaleY: inverseScale,
        });

        // Then compensate stroke width since group scaling affects it
        [topTickGroup, bottomTickGroup].forEach((group) => {
          group.getObjects().forEach((obj) => {
            if (obj.type === "line") {
              obj.set({
                strokeWidth: tickLineThickness * inverseScale,
              });
            }
          });
        });

        const aggregateFunctionLabel = findObjectById(
          fabricCanvas,
          annotationToolObjectIDs.AGGREGATE_LINE_LABEL
        ) as FabricText;

        if (aggregateLineY.current && aggregateFunctionLabel) {
          aggregateFunctionLabel.set({
            scaleX: inverseScale,
            scaleY: inverseScale,
            top:
              aggregateLineY.current - 16 * inverseScale - 0.04 / inverseScale,
          });
        }

        const aggregateLine = findObjectById(
          fabricCanvas,
          annotationToolObjectIDs.AGGREGATE_LINE
        ) as FabricText;

        if (aggregateLineY.current && aggregateLine) {
          aggregateLine.set({
            strokeWidth: aggregateLineThickness * inverseScale,
          });
        }
      }

      // Adjust brush width to maintain consistent visual thickness regardless of zoom
      if (fabricCanvas.freeDrawingBrush) {
        fabricCanvas.freeDrawingBrush.width = 1 / zoom;
      }

      // Adjust drawn path stroke width to maintain consistent visual thickness
      const drawnPathObject = findObjectById(
        fabricCanvas,
        annotationToolObjectIDs.DRAWN_PATH
      ) as Path;

      if (drawnPathObject) {
        drawnPathObject.set({
          strokeWidth: 1 / zoom,
        });
      }

      opt.e.preventDefault();
      opt.e.stopPropagation();
      fabricCanvas.renderAll();
    };

    const handleMouseDown = (opt: TPointerEventInfo<TPointerEvent>) => {
      const evt = opt.e;

      // If in drawing mode, let fabric handle drawing
      if (isDrawingEnabled) {
        return;
      }

      // Only enable panning if no object is being clicked
      // This allows dragging of tick groups and other objects
      if (
        (opt.target === null || opt.target === undefined) &&
        (evt instanceof MouseEvent || evt instanceof PointerEvent)
      ) {
        isDragging.current = true;
        fabricCanvas.selection = false;
        fabricCanvas.skipTargetFind = true;
        lastPosX.current = evt.clientX;
        lastPosY.current = evt.clientY;
      }
    };

    const handleMouseMove = (opt: TPointerEventInfo<TPointerEvent>) => {
      if (isDragging.current) {
        const evt = opt.e;
        const vpt = fabricCanvas.viewportTransform;
        if (evt instanceof MouseEvent || evt instanceof PointerEvent) {
          vpt[4] += evt.clientX - lastPosX.current;
          vpt[5] += evt.clientY - lastPosY.current;
          fabricCanvas.requestRenderAll();
          lastPosX.current = evt.clientX;
          lastPosY.current = evt.clientY;
        }
      }
    };

    const handleMouseUp = () => {
      // on mouse up we want to recalculate new interaction
      // for all objects, so we call setViewportTransform
      fabricCanvas.setViewportTransform(fabricCanvas.viewportTransform);
      isDragging.current = false;
      fabricCanvas.selection = true;
      fabricCanvas.skipTargetFind = false;
    };

    if (fabricCanvas) {
      // Add event listeners
      fabricCanvas.on("mouse:wheel", handleMouseWheel);
      fabricCanvas.on("mouse:down", handleMouseDown);
      fabricCanvas.on("mouse:move", handleMouseMove);
      fabricCanvas.on("mouse:up", handleMouseUp);
    }

    return () => {
      if (!fabricCanvas) return;
      // Remove specific handlers using named functions
      fabricCanvas.off("mouse:wheel", handleMouseWheel);
      fabricCanvas.off("mouse:down", handleMouseDown);
      fabricCanvas.off("mouse:move", handleMouseMove);
      fabricCanvas.off("mouse:up", handleMouseUp);
    };
  }, [fabricCanvas, isDrawingEnabled]);

  // Add event listeners to the canvas for blocking half of the image
  useEffect(() => {
    if (!fabricCanvas || !isCanvasReady) return;
    addHalfImageBlockerRectangle(fabricCanvas, focusedHalf);
  }, [fabricCanvas, focusedHalf, isCanvasReady]);

  // Reset the canvas when enabled:
  useEffect(() => {
    if (!fabricCanvas) return;

    hideTicks();
    resetView(fabricCanvas);

    findObjectsByIds(fabricCanvas, [
      annotationToolObjectIDs.DRAWN_PATH,
      annotationToolObjectIDs.AGGREGATE_LINE,
      annotationToolObjectIDs.AGGREGATE_LINE_LABEL,
    ]).forEach((object) => {
      if (object) fabricCanvas.remove(object);
    });

    clearDrawnPath();
  }, [enabled]);

  // Add event listeners to the canvas for drawing the aggregate line and label after drawing the path
  useEffect(() => {
    if (!drawnPath) return;
    const points = getPixelValuesFromPath(drawnPath);
    if (!points || !fabricCanvas || !aggregateFunction) return;
    if (points.length === 0) return;
    const zoom = fabricCanvas.getZoom();
    const aggregateYValue = aggregateFunctions[aggregateFunction](points);

    aggregateLineY.current = aggregateYValue;

    const aggregateLine = new Line(
      [0, aggregateYValue, fabricCanvas.width, aggregateYValue],
      {
        stroke: "green",
        strokeWidth: aggregateLineThickness / zoom,
        strokeUniform: true,
        hasBorders: false,
        hasControls: false,
        ...previewOnlyAttributes,
      }
    );

    const labelX =
      points.reduce((sum, point) => sum + point.x, 0) / points.length;

    const aggregateFunctionLabel = new FabricText(aggregateFunction, {
      left: labelX - 16,
      top: aggregateYValue - 16 / zoom - 0.04 * zoom,
      /** Fabric 7 defaults origin to center; use left/top so label is positioned by top-left. */
      originX: "left",
      originY: "top",
      fontSize: 12,
      fontFamily: "IBM Plex Sans",
      fill: "green",
      scaleX: 1 / zoom,
      scaleY: 1 / zoom,
      ...previewOnlyAttributes,
    });

    aggregateLine.set("id", annotationToolObjectIDs.AGGREGATE_LINE);

    aggregateFunctionLabel.set(
      "id",
      annotationToolObjectIDs.AGGREGATE_LINE_LABEL
    );

    const existingAggregateLine = findObjectById(
      fabricCanvas,
      annotationToolObjectIDs.AGGREGATE_LINE
    );

    if (existingAggregateLine) {
      fabricCanvas.remove(existingAggregateLine);
    }
    const existingAggregateFunctionLabel = findObjectById(
      fabricCanvas,
      annotationToolObjectIDs.AGGREGATE_LINE_LABEL
    );

    if (existingAggregateFunctionLabel) {
      fabricCanvas.remove(existingAggregateFunctionLabel);
    }

    fabricCanvas.add(aggregateLine);
    fabricCanvas.add(aggregateFunctionLabel);
  }, [drawnPath, fabricCanvas, aggregateFunction]);

  // Add event listeners to the canvas for disabling drawing mode if the canDraw prop is false
  useEffect(() => {
    if (!canDraw && isDrawingEnabled) {
      setIsDrawingEnabled(false);
      disableDrawingMode();
    }
  }, [canDraw]);

  const disableDrawingMode = () => {
    if (!fabricCanvas) return;
    setIsDrawingEnabled(false);
    fabricCanvas.isDrawingMode = false;
    if (pathCreatedHandlerRef.current) {
      fabricCanvas.off("path:created", pathCreatedHandlerRef.current);
      pathCreatedHandlerRef.current = null;
    }
  };

  const enableDrawingMode = () => {
    if (!fabricCanvas) return;
    setIsDrawingEnabled(true);
    fabricCanvas.isDrawingMode = true;

    // Create and configure the brush manually
    const brush = new PencilBrush(fabricCanvas);
    const zoom = fabricCanvas.getZoom();
    brush.width = 1 / zoom; // Adjust brush width based on current zoom level
    brush.color = "yellow";

    // Set the brush on the canvas
    fabricCanvas.freeDrawingBrush = brush;

    // Define the path created handler
    const pathCreatedHandler = ({ path }: { path: FabricObject }) => {
      // Remove all existing path objects except the newly created one
      const objects = fabricCanvas.getObjects();
      const pathObjects = objects.filter(
        (obj) => obj.type === "path"
      ) as Path[];

      // If there are multiple paths, remove all except the newest one (the event.path)
      pathObjects.forEach((pathObj) => {
        if (pathObj !== path) {
          fabricCanvas.remove(pathObj);
        } else {
          // Make the path non-interactive - disable all controls and movement
          pathObj.set(previewOnlyAttributes);

          setDrawnPath(pathObj);
          pathObj.set("id", annotationToolObjectIDs.DRAWN_PATH);
          disableDrawingMode();

          const points = getPixelValuesFromPath(pathObj);
          if (!points) return;
          updateActiveMeasurement({ measurementPoints: points });
        }
      });
    };

    // Store handler reference and add event listener
    pathCreatedHandlerRef.current = pathCreatedHandler;
    fabricCanvas.on("path:created", pathCreatedHandler);
  };

  const getPixelValuesFromPath = (path?: Path) => {
    if (!path) return;

    // Extract points using proper parsing of path commands
    const points: XY[] = [];
    let currentPosition: XY = { x: 0, y: 0 };

    path.path.forEach((pathTuple) => {
      const [command, ...coords] = pathTuple;

      switch (command) {
        case "M": // Move command: [type, x, y]
          currentPosition = { x: coords[0]!, y: coords[1]! };
          addUniquePixelPoint(points, currentPosition);
          break;

        case "L": // Line command: [type, x, y]
          currentPosition = { x: coords[0]!, y: coords[1]! };
          addUniquePixelPoint(points, currentPosition);
          break;

        case "Q": // Quadratic curve: [type, x1, y1, x, y]
          if (highDetailedBezierCurveSampling) {
            const curvePoints = sampleQuadraticCurve(
              currentPosition,
              { x: coords[0]!, y: coords[1]! },
              { x: coords[2]!, y: coords[3]! }
            );
            curvePoints.forEach((point) => addUniquePixelPoint(points, point));
          } else {
            addUniquePixelPoint(points, { x: coords[2]!, y: coords[3]! });
          }

          currentPosition = { x: coords[2]!, y: coords[3]! };
          break;

        default:
          console.warn(`Unknown path command: ${command}`, pathTuple);
      }
    });

    return points;
  };

  const bringTickIntoView = (position: "top" | "bottom") => {
    if (!fabricCanvas) return;

    const tickGroup = findObjectById(
      fabricCanvas,
      position === "top"
        ? annotationToolObjectIDs.TOP_TICK_GROUP
        : annotationToolObjectIDs.BOTTOM_TICK_GROUP
    ) as Group;

    if (tickGroup) {
      // Get current viewport transform
      const vpt = fabricCanvas.viewportTransform;
      const zoom = fabricCanvas.getZoom();

      // Calculate the visible area in canvas coordinates
      const canvasWidth = fabricCanvas.width;
      const canvasHeight = fabricCanvas.height;

      // Calculate the center of the visible viewport in canvas coordinates
      const viewportCenterX =
        (-vpt[4] +
          (focusedHalf === "left" ? 0 : canvasWidth / 3) +
          canvasWidth / 4) /
        zoom;
      const viewportCenterY = (-vpt[5] + canvasHeight / 2) / zoom;

      // Calculate the viewport dimensions in canvas coordinates
      const viewportHeight = canvasHeight / zoom;

      // Position top tick at 50% width, 30% height of the visible viewport
      // Position bottom tick at 50% width, 70% height of the visible viewport
      const tickX = viewportCenterX;
      const tickY =
        viewportCenterY - viewportHeight * 0.2 * (position === "top" ? 1 : -1); // 30% from top = center - 20%

      // Update positions
      tickGroup.set({
        left: tickX,
        top: tickY,
      });
      tickGroup.setCoords();

      // Render the canvas to show the changes
      fabricCanvas.requestRenderAll();

      updateTickVisibility(position, true);
    }
  };

  const hideTicks = () => {
    if (!fabricCanvas) return;

    ["top", "bottom"].forEach((position) => {
      const tickGroup = findObjectById(
        fabricCanvas,
        position === "top"
          ? annotationToolObjectIDs.TOP_TICK_GROUP
          : annotationToolObjectIDs.BOTTOM_TICK_GROUP
      ) as Group;

      tickGroup.set({
        left: -10000,
        top: -10000,
      });
      tickGroup.setCoords();
    });
    fabricCanvas.requestRenderAll();
    setTickVisibility({ top: false, bottom: false });
  };

  return (
    <div style={{ width: "fit-content", display: "flex", gap: 32 }}>
      <div style={{ position: "relative", display: "inline-block" }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={800}
          style={{ border: "1px solid black" }}
        />
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "2px solid #1D2C34",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <LoadingState
              label="Loading..."
              indicator={<Brandmark isAnimated />}
            />
          </div>
        )}
      </div>
      <div
        style={{
          height: "fit-content",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Card style={{ minWidth: 400 }}>
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {enabled && (
              <>
                {canDraw && (
                  <>
                    <Button
                      size="small"
                      variant={isDrawingEnabled ? "primary" : "outline"}
                      onClick={() => {
                        if (isDrawingEnabled) {
                          disableDrawingMode();
                        } else if (drawnPath) {
                          // Draw Again: clear meniscus, aggregate line and value so user can draw again; reset state so Next is disabled
                          if (fabricCanvas) {
                            findObjectsByIds(fabricCanvas, [
                              annotationToolObjectIDs.DRAWN_PATH,
                              annotationToolObjectIDs.AGGREGATE_LINE,
                              annotationToolObjectIDs.AGGREGATE_LINE_LABEL,
                            ]).forEach((obj) => {
                              if (obj) fabricCanvas.remove(obj);
                            });
                            fabricCanvas.requestRenderAll();
                            hideTicks();
                          }
                          aggregateLineY.current = undefined;
                          clearDrawnPath();
                          updateActiveMeasurement({
                            measurementPoints: [],
                            topTickY: -1,
                            bottomTickY: -1,
                            topTickX: -1,
                            bottomTickX: -1,
                            topTickValue: 0,
                            bottomTickValue: 0,
                          });
                          setTickVisibility({ top: false, bottom: false });
                          enableDrawingMode();
                        } else if (canDraw) {
                          enableDrawingMode();
                        }
                      }}
                      slotLeft={
                        <Icon
                          variant={
                            isDrawingEnabled ? "pencil-simple" : "pencil-simple"
                          }
                        />
                      }
                    >
                      {isDrawingEnabled
                        ? "Drawing Mode"
                        : drawnPath
                          ? "Draw Again"
                          : "Enable Drawing"}
                    </Button>
                    <DropdownMenu
                      options={[
                        {
                          label: "Average",
                          onClick: () =>
                            updateActiveMeasurement({
                              aggregateFunction: "average",
                            }),
                        },
                        {
                          label: "Minimum",
                          onClick: () =>
                            updateActiveMeasurement({
                              aggregateFunction: "minimum",
                            }),
                        },
                        {
                          label: "Maximum",
                          onClick: () =>
                            updateActiveMeasurement({
                              aggregateFunction: "maximum",
                            }),
                        },
                      ]}
                    >
                      <Button
                        icon="function"
                        variant="outline"
                        size="small"
                      >
                        {aggregateFunction}
                      </Button>
                    </DropdownMenu>
                  </>
                )}
                {canAddTicks && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        bringTickIntoView("top");
                      }}
                      slotLeft={
                        <Icon
                          variant={
                            tickVisibility.top
                              ? "check-circle"
                              : "caret-line-up"
                          }
                        />
                      }
                      disabled={tickVisibility.top}
                    >
                      Place Top Tick
                    </Button>
                    <Button
                      onClick={() => {
                        bringTickIntoView("bottom");
                      }}
                      slotLeft={
                        <Icon
                          variant={
                            tickVisibility.bottom
                              ? "check-circle"
                              : "caret-line-down"
                          }
                        />
                      }
                      disabled={tickVisibility.bottom}
                    >
                      Place Bottom Tick
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
          {enabled && <hr style={{ margin: "16px 0", width: "100%" }} />}
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <Button
              onClick={() => {
                toggleBackgroundImage();
              }}
              slotLeft={<Icon variant="file-image" />}
              variant="outline"
            >
              {currentBackgroundImage === "regular" ? "UV" : "Regular"}
            </Button>
            <Button
              onClick={() => {
                if (fabricCanvas) resetView(fabricCanvas);
              }}
              slotLeft={<Icon variant="magnifying-glass" />}
            >
              Reset view
            </Button>
          </div>
        </Card>
        <Card
          style={{
            width: 400,
            fontSize: "14px",
            color: "#82939A",
            fontWeight: "400",
          }}
        >
          Guide
          <hr style={{ width: "100%", margin: "8px 0" }} />
          {`You can zoom in and out using mouse wheel and click and drag the image
          around. Pressing "UV"/"Regular" buttons will switch the between regular and UV photo. Pressing "Reset View" button will reset the zoom and center the image.`}
        </Card>
      </div>
    </div>
  );
};
export default NewAnnotationTool;
